(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const STORAGE_ZIP = 'anx_zip';
  const STORAGE_DEST = 'anx_dest_full';
  const STORAGE_PLAN_MODE = 'aonsk_collection_finder_plan_mode';
  const STORAGE_PLAN_DATE = 'aonsk_collection_finder_plan_date';

  const normalizeText = (value) => {
    if (Array.isArray(value)) return value.join(' ').toLowerCase();
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return Object.values(value).join(' ').toLowerCase();
    return String(value).toLowerCase();
  };

  const normalizeForMatch = (value) => normalizeText(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const containsPhrase = (text, phrase) => {
    const haystack = ` ${normalizeForMatch(text)} `;
    const needleText = normalizeForMatch(phrase);
    if (!needleText) return false;
    if (haystack.includes(` ${needleText} `)) return true;
    if (needleText.endsWith('s') && needleText.length > 3 && haystack.includes(` ${needleText.slice(0, -1)} `)) return true;
    if (!needleText.endsWith('s') && haystack.includes(` ${needleText}s `)) return true;
    return false;
  };


  const CATEGORY_STOP_WORDS = new Set([
    'seed', 'seeds', 'collection', 'collections', 'garden', 'gardens', 'home', 'heirloom',
    'native', 'all', 'the', 'and', 'for', 'of', 'plant', 'plants', 'vegetable', 'vegetables'
  ]);

  const FALLBACK_CROP_TERMS = [
    ['watermelon', 'Watermelon'], ['cantaloupe', 'Cantaloupe'], ['muskmelon', 'Muskmelon'],
    ['honeydew', 'Honeydew melon'], ['melon', 'Melon'], ['tomato', 'Tomato'],
    ['pepper', 'Pepper'], ['eggplant', 'Eggplant'], ['cucumber', 'Cucumber'],
    ['pumpkin', 'Pumpkin'], ['squash', 'Squash'], ['zucchini', 'Zucchini'],
    ['bean', 'Bean'], ['cowpea', 'Cowpea'], ['pea', 'Pea'], ['corn', 'Corn'],
    ['okra', 'Okra'], ['cauliflower', 'Cauliflower'], ['broccoli', 'Broccoli'],
    ['cabbage', 'Cabbage'], ['kale', 'Kale'], ['collard', 'Collard'],
    ['lettuce', 'Lettuce'], ['spinach', 'Spinach'], ['chard', 'Chard'],
    ['arugula', 'Arugula'], ['mustard', 'Mustard greens'], ['carrot', 'Carrot'],
    ['beet', 'Beet'], ['radish', 'Radish'], ['turnip', 'Turnip'], ['onion', 'Onion'],
    ['leek', 'Leek'], ['garlic', 'Garlic'], ['basil', 'Basil'], ['cilantro', 'Cilantro'],
    ['parsley', 'Parsley'], ['dill', 'Dill'], ['thyme', 'Thyme'], ['sage', 'Sage'],
    ['oregano', 'Oregano'], ['mint', 'Mint'], ['sunflower', 'Sunflower'], ['zinnia', 'Zinnia']
  ];

  const titleCase = (value) => String(value || '').replace(/\b\w/g, (letter) => letter.toUpperCase());

  const categoryParts = (label) => {
    const normalized = normalizeForMatch(label);
    const tokens = normalized.split(' ').filter((token) => token.length >= 3 && !CATEGORY_STOP_WORDS.has(token));
    const core = tokens.join(' ');
    return { normalized, tokens, core };
  };

  const numberFrom = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const match = String(value).match(/\d+/);
    return match ? Number(match[0]) : null;
  };

  const parseRange = (label) => {
    const numbers = String(label || '').match(/\d+/g) || [];
    if (!numbers.length) return { min: null, max: null };
    if (numbers.length === 1) {
      const value = Number(numbers[0]);
      return { min: value, max: /\+|over|more/i.test(label) ? 9999 : value };
    }
    return { min: Number(numbers[0]), max: Number(numbers[1]) };
  };

  const hashNumber = (value) => {
    let hash = 0;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash = ((hash << 5) - hash) + input.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + Number(days || 0));
    return result;
  };

  const daysBetween = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);

  const formatDate = (date, options = {}) => date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: options.year ? 'numeric' : undefined
  });

  const dateFromInput = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const dateFromMonthDay = (monthDay, year) => {
    const parts = String(monthDay || '').split('-').map(Number);
    if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
    return new Date(year, parts[0] - 1, parts[1]);
  };

  const BROAD_COLLECTION_KINDS = new Set(['seed', 'vegetable', 'herb', 'flower', 'native', 'companion', 'grain']);
  const BROAD_PROFILE_PREFIXES = ['department-', 'catalog-', 'purpose-', 'space-', 'season-', 'regional-', 'native-state-', 'climate-zone-'];
  const BROAD_PROFILES = new Set([
    'native-catalog', 'native-flower', 'native-perennial', 'native-trees-shrubs',
    'herb-culinary', 'herb-medicinal', 'herb-tea', 'perennial-herbs', 'perennial-flowers',
    'salad-greens', 'specialty-greens', 'asian-greens', 'specialty-melon'
  ]);
  const isBroadFinderProfile = (profile, kind) => {
    const normalized = normalizeForMatch(profile).replace(/ /g, '-');
    if (normalized) {
      if (BROAD_PROFILES.has(normalized)) return true;
      if (BROAD_PROFILE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
      return false;
    }
    return BROAD_COLLECTION_KINDS.has(kind || '');
  };

  const CROP_RULES = [
    { id: 'watermelon', label: 'watermelon', aliases: ['watermelon', 'watermelons'] },
    { id: 'cantaloupe', label: 'cantaloupe or muskmelon', aliases: ['cantaloupe', 'cantaloupes', 'muskmelon', 'muskmelons', 'honeydew'] },
    { id: 'melon', label: 'melon', aliases: ['melon', 'melons'] },
    { id: 'tomato', label: 'tomato', aliases: ['tomato', 'tomatoes'] },
    { id: 'pepper', label: 'pepper or chile', aliases: ['pepper', 'peppers', 'chile', 'chiles', 'chili', 'chilies'] },
    { id: 'eggplant', label: 'eggplant', aliases: ['eggplant', 'eggplants', 'aubergine'] },
    { id: 'okra', label: 'okra', aliases: ['okra'] },
    { id: 'corn', label: 'corn', aliases: ['corn', 'maize'] },
    { id: 'cowpea', label: 'cowpea', aliases: ['cowpea', 'cowpeas', 'black eyed pea', 'black eyed peas'] },
    { id: 'bean', label: 'bean', aliases: ['bean', 'beans', 'lima bean', 'lima beans', 'runner bean', 'runner beans'] },
    { id: 'pea', label: 'pea', aliases: ['pea', 'peas', 'garden pea', 'garden peas', 'snap pea', 'snap peas', 'snow pea', 'snow peas'] },
    { id: 'cucumber', label: 'cucumber', aliases: ['cucumber', 'cucumbers'] },
    { id: 'pumpkin', label: 'pumpkin', aliases: ['pumpkin', 'pumpkins'] },
    { id: 'squash', label: 'squash', aliases: ['squash', 'summer squash', 'winter squash', 'zucchini'] },
    { id: 'gourd', label: 'gourd', aliases: ['gourd', 'gourds'] },
    { id: 'broccoli', label: 'broccoli', aliases: ['broccoli', 'broccoli rabe', 'rapini'] },
    { id: 'cauliflower', label: 'cauliflower', aliases: ['cauliflower', 'cauliflowers'] },
    { id: 'brussels-sprout', label: 'Brussels sprouts', aliases: ['brussels sprout', 'brussels sprouts'] },
    { id: 'cabbage', label: 'cabbage', aliases: ['cabbage', 'cabbages'] },
    { id: 'collard', label: 'collards', aliases: ['collard', 'collards', 'collard greens'] },
    { id: 'kale', label: 'kale', aliases: ['kale'] },
    { id: 'kohlrabi', label: 'kohlrabi', aliases: ['kohlrabi'] },
    { id: 'lettuce', label: 'lettuce', aliases: ['lettuce', 'lettuces'] },
    { id: 'spinach', label: 'spinach', aliases: ['spinach'] },
    { id: 'chard', label: 'chard', aliases: ['chard', 'swiss chard'] },
    { id: 'arugula', label: 'arugula', aliases: ['arugula', 'rocket'] },
    { id: 'mustard', label: 'mustard greens', aliases: ['mustard green', 'mustard greens', 'mustard'] },
    { id: 'bok-choy', label: 'bok choy or pak choi', aliases: ['bok choy', 'pak choi', 'pac choi'] },
    { id: 'endive', label: 'endive', aliases: ['endive', 'endives'] },
    { id: 'chicory', label: 'chicory', aliases: ['chicory', 'radicchio'] },
    { id: 'carrot', label: 'carrot', aliases: ['carrot', 'carrots'] },
    { id: 'beet', label: 'beet', aliases: ['beet', 'beets'] },
    { id: 'radish', label: 'radish', aliases: ['radish', 'radishes'] },
    { id: 'turnip', label: 'turnip', aliases: ['turnip', 'turnips'] },
    { id: 'parsnip', label: 'parsnip', aliases: ['parsnip', 'parsnips'] },
    { id: 'rutabaga', label: 'rutabaga', aliases: ['rutabaga', 'rutabagas'] },
    { id: 'onion', label: 'onion', aliases: ['onion', 'onions'] },
    { id: 'leek', label: 'leek', aliases: ['leek', 'leeks'] },
    { id: 'scallion', label: 'scallion', aliases: ['scallion', 'scallions', 'green onion', 'green onions', 'bunching onion', 'bunching onions'] },
    { id: 'garlic', label: 'garlic', aliases: ['garlic'] },
    { id: 'basil', label: 'basil', aliases: ['basil', 'basils', 'tulsi', 'holy basil'] },
    { id: 'cilantro', label: 'cilantro or coriander', aliases: ['cilantro', 'coriander'] },
    { id: 'parsley', label: 'parsley', aliases: ['parsley'] },
    { id: 'dill', label: 'dill', aliases: ['dill'] },
    { id: 'thyme', label: 'thyme', aliases: ['thyme'] },
    { id: 'oregano', label: 'oregano', aliases: ['oregano'] },
    { id: 'rosemary', label: 'rosemary', aliases: ['rosemary'] },
    { id: 'sage', label: 'sage', aliases: ['sage'] },
    { id: 'mint', label: 'mint', aliases: ['mint', 'mints', 'peppermint', 'spearmint'] },
    { id: 'lavender', label: 'lavender', aliases: ['lavender', 'lavenders'] },
    { id: 'chamomile', label: 'chamomile', aliases: ['chamomile'] },
    { id: 'sunflower', label: 'sunflower', aliases: ['sunflower', 'sunflowers'] },
    { id: 'zinnia', label: 'zinnia', aliases: ['zinnia', 'zinnias'] },
    { id: 'cosmos', label: 'cosmos', aliases: ['cosmos'] },
    { id: 'marigold', label: 'marigold', aliases: ['marigold', 'marigolds'] },
    { id: 'milkweed', label: 'milkweed', aliases: ['milkweed', 'milkweeds'] },
    { id: 'echinacea', label: 'echinacea or coneflower', aliases: ['echinacea', 'coneflower', 'coneflowers'] },
    { id: 'yarrow', label: 'yarrow', aliases: ['yarrow'] },
    { id: 'amaranth', label: 'amaranth', aliases: ['amaranth', 'amaranths'] },
    { id: 'grain', label: 'grain', aliases: ['grain', 'grains', 'wheat', 'rye', 'oats', 'barley'] }
  ];

  const COOL_CROP_IDS = new Set([
    'broccoli', 'cauliflower', 'brussels-sprout', 'cabbage', 'collard', 'kale', 'kohlrabi',
    'lettuce', 'spinach', 'chard', 'arugula', 'mustard', 'bok-choy', 'endive', 'chicory',
    'carrot', 'beet', 'radish', 'turnip', 'parsnip', 'rutabaga', 'onion', 'leek', 'scallion',
    'garlic', 'pea', 'cilantro', 'parsley', 'dill'
  ]);

  const WARM_CROP_IDS = new Set([
    'watermelon', 'cantaloupe', 'melon', 'tomato', 'pepper', 'eggplant', 'okra', 'corn',
    'cowpea', 'bean', 'cucumber', 'pumpkin', 'squash', 'gourd', 'basil', 'amaranth',
    'sunflower', 'zinnia', 'cosmos', 'marigold'
  ]);

  const cropSeasonClass = (query) => {
    const term = normalizeForMatch(query?.term || query?.label || '');
    const rule = CROP_RULES.find((item) => item.id === term || item.aliases.some((alias) => containsPhrase(term, alias)));
    if (!rule) return '';
    if (COOL_CROP_IDS.has(rule.id)) return 'cool';
    if (WARM_CROP_IDS.has(rule.id)) return 'warm';
    return '';
  };

  const stripHtml = (value) => String(value || '')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

  const detectCropRule = (text) => CROP_RULES.find((rule) => rule.aliases.some((alias) => containsPhrase(text, alias))) || null;

  const buildFrostTable = () => {
    const table = {};
    const fill = (start, end, last, first) => {
      for (let prefix = start; prefix <= end; prefix += 1) {
        table[String(prefix).padStart(3, '0')] = { last, first };
      }
    };

    fill(6, 9, '01-01', '12-31');
    fill(967, 968, '01-01', '12-31');
    fill(10, 27, '05-15', '10-10');
    fill(28, 29, '05-10', '10-15');
    fill(30, 38, '05-20', '10-05');
    fill(39, 49, '05-25', '10-01');
    fill(50, 59, '05-20', '10-05');
    fill(60, 69, '04-25', '10-25');
    fill(70, 89, '04-20', '10-25');
    fill(100, 119, '04-20', '10-25');
    fill(120, 149, '05-10', '10-10');
    fill(150, 169, '04-30', '10-20');
    fill(170, 196, '04-25', '10-25');
    fill(197, 199, '04-10', '11-01');
    fill(200, 205, '04-05', '11-05');
    fill(206, 219, '04-05', '10-31');
    fill(220, 246, '04-10', '10-31');
    fill(247, 268, '04-30', '10-20');
    fill(270, 289, '04-05', '11-15');
    fill(290, 299, '03-30', '11-20');
    fill(300, 319, '03-25', '11-18');
    fill(398, 399, '03-25', '11-18');
    fill(320, 339, '02-15', '12-10');
    fill(340, 349, '02-01', '12-20');
    fill(350, 369, '03-25', '11-10');
    fill(386, 397, '03-25', '11-10');
    fill(370, 385, '04-15', '10-31');
    fill(400, 427, '04-20', '10-20');
    fill(430, 459, '05-10', '10-15');
    fill(460, 479, '05-10', '10-10');
    fill(480, 497, '05-20', '10-05');
    fill(498, 499, '06-01', '09-20');
    fill(500, 529, '05-05', '10-05');
    fill(530, 549, '05-15', '10-05');
    fill(550, 567, '05-20', '09-30');
    fill(600, 629, '04-25', '10-20');
    fill(630, 658, '04-20', '10-20');
    fill(570, 577, '05-15', '09-30');
    fill(580, 588, '05-25', '09-20');
    fill(660, 679, '04-20', '10-20');
    fill(680, 693, '05-10', '10-05');
    fill(700, 714, '02-25', '11-20');
    fill(716, 729, '03-30', '11-05');
    fill(730, 749, '04-10', '10-30');
    fill(750, 769, '03-20', '11-10');
    fill(770, 775, '02-25', '11-25');
    fill(776, 799, '03-15', '11-15');
    fill(870, 884, '04-15', '10-25');
    fill(850, 853, '02-20', '12-05');
    fill(854, 858, '04-10', '11-15');
    fill(859, 865, '05-20', '10-01');
    fill(800, 816, '05-20', '09-30');
    fill(820, 831, '06-01', '09-15');
    fill(590, 599, '05-30', '09-20');
    fill(832, 838, '05-15', '10-05');
    fill(840, 847, '05-10', '10-10');
    fill(889, 891, '02-20', '12-10');
    fill(894, 898, '05-10', '10-05');
    table['892'] = { last: '04-20', first: '10-10' };
    table['893'] = { last: '04-20', first: '10-10' };
    fill(900, 916, '02-20', '12-10');
    fill(917, 919, '03-05', '11-30');
    fill(920, 921, '02-10', '12-15');
    table['922'] = { last: '02-01', first: '12-20' };
    fill(923, 925, '03-10', '11-25');
    fill(926, 928, '02-15', '12-10');
    fill(930, 935, '03-01', '11-25');
    fill(936, 939, '03-10', '11-25');
    fill(940, 949, '03-15', '11-20');
    fill(950, 959, '03-15', '11-10');
    fill(960, 961, '04-25', '10-20');
    fill(970, 973, '04-20', '10-25');
    fill(974, 976, '04-25', '10-20');
    fill(977, 979, '05-05', '10-10');
    fill(980, 986, '04-20', '10-25');
    fill(988, 989, '05-05', '10-10');
    fill(990, 994, '05-05', '10-10');
    fill(995, 999, '06-05', '08-30');
    table['27924'] = { last: '04-05', first: '11-15' };
    table['27983'] = { last: '04-05', first: '11-15' };
    return table;
  };

  const FROST = buildFrostTable();

  class AonskSeedFinder extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.steps = [];
      this.currentStep = 0;
      this.naturalQuery = '';
      this.specificQuery = null;
      this.syntheticCategory = null;
      this.recommendedMaturityInput = null;
      this.catalogProducts = null;
      this.catalogPromise = null;
      this.catalogFetchFailed = false;
      this.searchCache = new Map();
      this.isFinishing = false;
    }

    connectedCallback() {
      if (this.dataset.finderInitialized === 'true') return;
      this.dataset.finderInitialized = 'true';

      const configNode = this.querySelector('[data-finder-config]');
      if (!configNode) return;

      try {
        this.config = JSON.parse(configNode.textContent);
        this.useSeasonTools = this.config.useSeasonTools !== false;
      } catch (error) {
        console.warn('Guided collection finder configuration could not be read.', error);
        return;
      }

      this.dialog = this.querySelector('[data-finder-dialog]');
      this.results = this.querySelector('[data-finder-results]');
      this.resultGrid = this.querySelector('[data-finder-result-grid]');
      this.laterGrid = this.querySelector('[data-finder-later-grid]');
      this.laterWrap = this.querySelector('[data-finder-later-wrap]');
      this.resultsSummary = this.querySelector('[data-finder-results-summary]');
      this.resultsHeading = this.querySelector('[data-finder-results-heading]');
      this.seasonResult = this.querySelector('[data-finder-season-result]');
      this.browseLink = this.querySelector('[data-finder-browse]');
      this.note = this.querySelector('[data-finder-note]');
      this.queryRecognition = this.querySelector('[data-finder-query-recognition]');
      this.noteForm = this.querySelector('[data-finder-note-form]');
      this.progressBar = this.querySelector('[data-finder-progress-bar]');
      this.stepCount = this.querySelector('[data-finder-step-count]');
      this.backButton = this.querySelector('[data-finder-back]');
      this.skipButton = this.querySelector('[data-finder-skip]');
      this.nextButton = this.querySelector('[data-finder-next]');
      this.finishButton = this.querySelector('[data-finder-finish]');
      this.zipInput = this.querySelector('[data-finder-zip]');
      this.planSelect = this.querySelector('[data-finder-plan-select]');
      this.dateInput = this.querySelector('[data-finder-date]');
      this.dateWrap = this.querySelector('[data-finder-date-wrap]');
      this.todayLabel = this.querySelector('[data-finder-today]');
      this.seasonPreview = this.querySelector('[data-finder-season-preview]');
      this.seasonError = this.querySelector('[data-finder-season-error]');
      this.dialogContext = this.querySelector('[data-finder-dialog-context]');
      this.editContextButton = this.querySelector('[data-finder-edit-context]');
      this.maturityContext = this.querySelector('[data-finder-maturity-context]');
      this.maturityContextHeading = this.querySelector('[data-finder-maturity-heading]');
      this.maturityContextCopy = this.querySelector('[data-finder-maturity-copy]');
      this.maturityUseButton = this.querySelector('[data-finder-use-maturity]');
      this.maturityAutoInput = this.querySelector('[data-maturity-auto]');
      this.maturityAutoCopy = this.querySelector('[data-maturity-auto-copy]');
      this.pruneIntentChoices();
      this.steps = Array.from(this.dialog?.querySelectorAll('[data-finder-step]') || []).filter((step) => !step.hidden);

      this.loadSavedContext();
      this.bindEvents();
      this.syncChoiceStates();
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateQueryRecognition();
      this.updateMaturityGuidance();
      this.updateStep();
    }

    isBroadCollection() {
      return isBroadFinderProfile(this.config.finderProfile, this.config.collectionKind);
    }

    pruneIntentChoices() {
      const step = this.querySelector('[data-finder-step="intent"]');
      if (!step || step.hidden) return;
      let visible = 0;
      step.querySelectorAll('.aonsk-finder__choice').forEach((choice) => {
        const input = choice.querySelector('input[data-intent-keywords]');
        if (!input) return;
        const intent = (this.config.intents || []).find((item) => item.id === input.value);
        if (!intent) return;
        const keywords = (intent.keywords || []).map((keyword) => normalizeText(keyword).trim()).filter(Boolean);
        const hasMatch = intent.specialMode === 'surprise' || (this.config.products || []).some((product) => {
          const text = this.productText(product);
          if (intent.specialMode === 'container') {
            const locationText = normalizeText(product.locations);
            if (product.containerSuitable === true || locationText.includes('container')) return true;
          }
          return keywords.some((keyword) => keyword && containsPhrase(text, keyword));
        });
        choice.hidden = !hasMatch;
        if (hasMatch) visible += 1;
      });
      if (visible < 2) step.hidden = true;
      this.querySelectorAll('[data-finder-quick]').forEach((button) => {
        const intentId = button.dataset.finderIntentId;
        if (!intentId) return;
        const matchingChoice = step.querySelector(`input[value="${CSS.escape(intentId)}"]`)?.closest('.aonsk-finder__choice');
        button.hidden = Boolean(matchingChoice?.hidden);
      });
    }

    bindEvents() {
      this.querySelectorAll('[data-finder-open]').forEach((button) => {
        button.addEventListener('click', () => this.prepareAndOpen());
      });

      this.querySelectorAll('[data-finder-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-finder-show-all]').forEach((button) => {
        button.addEventListener('click', () => this.scrollToProducts());
      });

      this.noteForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.prepareAndOpen();
      });


      this.note?.addEventListener('input', () => this.updateQueryRecognition());

      this.maturityUseButton?.addEventListener('click', () => {
        if (!this.recommendedMaturityInput) return;
        this.checkElement(this.recommendedMaturityInput);
        this.updateMaturityGuidance();
      });

      this.querySelectorAll('[data-finder-quick-match]').forEach((button) => {
        button.addEventListener('click', () => {
          this.applyNaturalNote(this.combinedNaturalNote());
          if (!this.validateSeasonStep()) {
            this.focusPlanning();
            return;
          }
          this.finish();
        });
      });

      this.querySelectorAll('[data-finder-note-example]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!this.note) return;
          this.note.value = button.dataset.finderNoteExample || '';
          this.updateQueryRecognition();
          this.note.focus();
        });
      });

      this.querySelectorAll('[data-finder-quick]').forEach((button) => {
        button.addEventListener('click', () => {
          const selected = button.getAttribute('aria-pressed') === 'true';
          button.setAttribute('aria-pressed', selected ? 'false' : 'true');
          button.classList.toggle('is-selected', !selected);
          this.updateQueryRecognition();
        });
      });

      this.querySelectorAll('[data-finder-experience]').forEach((button) => {
        button.addEventListener('click', () => {
          const selected = button.getAttribute('aria-pressed') === 'true';
          button.setAttribute('aria-pressed', selected ? 'false' : 'true');
          button.classList.toggle('is-selected', !selected);
        });
      });

      this.editContextButton?.addEventListener('click', () => this.focusPlanning());

      this.querySelectorAll('[data-finder-edit]').forEach((button) => {
        button.addEventListener('click', () => {
          this.updateDialogContext();
          this.open();
        });
      });

      this.querySelectorAll('[data-finder-reset]').forEach((button) => {
        button.addEventListener('click', () => this.reset());
      });

      this.backButton?.addEventListener('click', () => {
        this.currentStep = Math.max(0, this.currentStep - 1);
        this.updateStep();
      });

      this.skipButton?.addEventListener('click', () => this.skipCurrent());
      this.nextButton?.addEventListener('click', () => this.advance());
      this.finishButton?.addEventListener('click', () => this.finish());

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      this.querySelectorAll('.aonsk-finder__choice input').forEach((input) => {
        input.addEventListener('change', (event) => {
          const choice = input.closest('.aonsk-finder__choice');
          if (choice) {
            choice.closest('.aonsk-finder__step')?.querySelectorAll('.aonsk-finder__choice').forEach((item) => {
              item.classList.toggle('is-selected', Boolean(item.querySelector('input')?.checked));
            });
          }
          const stepName = input.closest('[data-finder-step]')?.dataset.finderStep;
          if (stepName === 'category' && event.isTrusted) {
            this.syntheticCategory = null;
            this.specificQuery = null;
            this.updateQueryRecognition();
          }
          if (input.dataset.planMode) {
            this.updateDateVisibility();
            this.savePlanningContext();
            this.updateSeasonPreview();
          }
          if (stepName === 'maturity') this.updateMaturityGuidance();
        });
      });

      this.zipInput?.addEventListener('input', () => {
        this.zipInput.value = this.zipInput.value.replace(/\D/g, '').slice(0, 5);
        this.clearSeasonError();
        this.updateSeasonPreview();
        this.updateMaturityGuidance();
        this.updateDialogContext();
      });

      this.zipInput?.addEventListener('change', () => {
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateMaturityGuidance();
        this.updateDialogContext();
      });

      this.planSelect?.addEventListener('change', () => {
        this.clearSeasonError();
        this.updateDateVisibility();
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateMaturityGuidance();
        this.updateDialogContext();
      });

      this.dateInput?.addEventListener('change', () => {
        this.clearSeasonError();
        this.savePlanningContext();
        this.updateSeasonPreview();
        this.updateMaturityGuidance();
        this.updateDialogContext();
      });
    }

    loadSavedContext() {
      if (this.todayLabel) {
        this.todayLabel.textContent = `Today is ${formatDate(new Date(), { year: true })}.`;
      }

      let savedZip = '';
      let savedMode = 'now';
      let savedDate = '';
      const sharedContext = window.AONSK_GARDEN_DATA?.readProfilePreferences?.('collectionFinder') || {};
      const sharedClimate = window.AONSK_GARDEN_DATA?.readClimateProfile?.();
      try {
        savedZip = sharedContext.zip || sharedClimate?.zip || localStorage.getItem(STORAGE_ZIP) || '';
        savedMode = sharedContext.mode || localStorage.getItem(STORAGE_PLAN_MODE) || 'now';
        savedDate = sharedContext.planDate || localStorage.getItem(STORAGE_PLAN_DATE) || '';
      } catch (error) {
        // The guide still works when browser storage is unavailable.
      }

      if (this.zipInput && /^\d{5}$/.test(savedZip)) this.zipInput.value = savedZip;
      if (this.planSelect) this.planSelect.value = savedMode;
      if (this.dateInput && savedDate) this.dateInput.value = savedDate;
    }

    savePlanningContext() {
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      const planDate = this.dateInput?.value || '';
      try {
        if (zip.length === 5) {
          localStorage.setItem(STORAGE_ZIP, zip);
          if (!localStorage.getItem(STORAGE_DEST)) localStorage.setItem(STORAGE_DEST, zip);
        }
        localStorage.setItem(STORAGE_PLAN_MODE, mode);
        if (planDate) localStorage.setItem(STORAGE_PLAN_DATE, planDate);
        else localStorage.removeItem(STORAGE_PLAN_DATE);
      } catch (error) {
        // Browser storage is optional.
      }
      window.AONSK_GARDEN_DATA?.updateProfilePreferences?.('collectionFinder', { zip, mode, planDate }, {
        sourceName: 'Collection seed finder',
        sourceVersion: '10.36'
      });
    }

    cleanZip(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 5);
    }

    combinedNaturalNote() {
      const parts = [];
      const written = String(this.note?.value || '').trim();
      if (written) parts.push(written);
      this.querySelectorAll('[data-finder-quick][aria-pressed="true"]').forEach((button) => {
        const phrase = String(button.dataset.finderQuickText || '').trim();
        if (phrase) parts.push(phrase);
      });
      return parts.join(', ');
    }

    prepareAndOpen() {
      this.applyNaturalNote(this.combinedNaturalNote());
      if (!this.validateSeasonStep()) {
        this.focusPlanning();
        return;
      }
      this.updateDialogContext();
      this.open();
    }

    focusPlanning() {
      this.close();
      if (!this.useSeasonTools) {
        this.close();
        const target = this.noteForm || this;
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        requestAnimationFrame(() => this.note?.focus());
        return;
      }
      const target = this.noteForm || this;
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      requestAnimationFrame(() => {
        const zip = this.cleanZip(this.zipInput?.value);
        const mode = this.planSelect?.value || 'now';
        if (zip && zip.length !== 5) this.zipInput?.focus();
        else if (mode === 'later' && !dateFromInput(this.dateInput?.value)) this.dateInput?.focus();
        else this.zipInput?.focus();
      });
    }

    updateDialogContext() {
      if (!this.dialogContext) return;
      if (!this.useSeasonTools) {
        this.dialogContext.textContent = 'Using your collection preferences';
        return;
      }
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      let timing = 'broad planting guidance';
      if (mode === 'now') timing = 'planting now';
      else if (mode === 'later') {
        const selectedDate = dateFromInput(this.dateInput?.value);
        timing = selectedDate ? `planting ${formatDate(selectedDate, { year: true })}` : 'another planting date';
      }
      else if (mode === 'next-year') timing = 'planning for next year';
      else if (mode === 'all-area') timing = 'viewing the full season';
      this.dialogContext.textContent = zip ? `Using ZIP ${zip} · ${timing}` : `No ZIP entered · ${timing}`;
    }

    open() {
      if (!this.dialog) return;
      this.currentStep = Math.min(this.currentStep, Math.max(0, this.steps.length - 1));
      this.updateStep();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('aonsk-finder-is-open');
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('aonsk-finder-is-open');
    }

    advance() {
      const step = this.steps[this.currentStep];
      if (this.currentStep >= this.steps.length - 1) {
        this.finish();
        return;
      }
      this.currentStep += 1;
      this.updateStep();
    }

    skipCurrent() {
      const step = this.steps[this.currentStep];
      if (!step) {
        this.advance();
        return;
      }

      const stepName = step.dataset.finderStep;
      if (stepName === 'availability') {
        const showAll = step.querySelector('[data-availability="all"]');
        if (showAll) this.checkElement(showAll);
      } else if (stepName === 'maturity') {
        const automatic = step.querySelector('[data-maturity-auto]');
        if (automatic) this.checkElement(automatic);
      } else {
        step.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
        step.querySelectorAll('.aonsk-finder__choice').forEach((choice) => {
          choice.classList.remove('is-selected');
        });
      }
      this.advance();
    }

    updateStep() {
      if (!this.steps.length) return;
      this.steps.forEach((step, index) => step.classList.toggle('is-active', index === this.currentStep));
      const isLast = this.currentStep === this.steps.length - 1;
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;
      if (this.nextButton) this.nextButton.hidden = isLast;
      if (this.finishButton) this.finishButton.hidden = !isLast;
      if (this.skipButton) {
        const stepName = this.steps[this.currentStep]?.dataset.finderStep;
        if (isLast) this.skipButton.textContent = 'Keep all availability';
        else this.skipButton.textContent = 'Skip this question';
      }
      const progress = ((this.currentStep + 1) / this.steps.length) * 100;
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;
      if (this.stepCount) this.stepCount.textContent = `Question ${this.currentStep + 1} of ${this.steps.length}`;
      const activeStep = this.steps[this.currentStep];
      if (activeStep?.dataset.finderStep === 'maturity') this.updateMaturityGuidance();
      const focusTarget = activeStep?.querySelector('input:not([type="hidden"]), textarea, button');
      focusTarget?.focus({ preventScroll: true });
    }

    selectedInput(stepName) {
      return this.querySelector(`[data-finder-step="${stepName}"] input[type="radio"]:checked`);
    }

    updateDateVisibility() {
      const mode = this.planSelect?.value || 'now';
      if (this.dateWrap) this.dateWrap.hidden = mode !== 'later';
      if (mode === 'later' && this.dateInput && !this.dateInput.value) {
        this.dateInput.value = toInputDate(addDays(new Date(), 30));
      }
    }

    validateSeasonStep() {
      if (!this.useSeasonTools) return true;
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      if (zip && zip.length !== 5) {
        this.showSeasonError('Enter a five-digit US ZIP code, or clear the field to continue without a ZIP-based estimate.');
        this.zipInput?.focus();
        return false;
      }
      if (mode === 'later' && !dateFromInput(this.dateInput?.value)) {
        this.showSeasonError('Choose the planting date you would like the guide to check.');
        this.dateInput?.focus();
        return false;
      }
      this.clearSeasonError();
      this.savePlanningContext();
      return true;
    }

    showSeasonError(message) {
      if (!this.seasonError) return;
      this.seasonError.textContent = message;
      this.seasonError.hidden = false;
    }

    clearSeasonError() {
      if (!this.seasonError) return;
      this.seasonError.textContent = '';
      this.seasonError.hidden = true;
    }

    getFrostRecord(zip) {
      const clean = this.cleanZip(zip);
      if (clean.length !== 5) return null;
      return FROST[clean] || FROST[clean.slice(0, 3)] || null;
    }

    buildSeasonContext() {
      if (!this.useSeasonTools) {
        return { record: null, mode: 'all-area', phase: 'unknown', zip: '', planDate: null };
      }
      const zip = this.cleanZip(this.zipInput?.value);
      const mode = this.planSelect?.value || 'now';
      const record = this.getFrostRecord(zip);
      const today = startOfDay(new Date());
      let planDate = null;
      let year = today.getFullYear();

      if (mode === 'now') planDate = today;
      if (mode === 'later') {
        planDate = dateFromInput(this.dateInput?.value);
        if (planDate) year = planDate.getFullYear();
      }
      if (mode === 'next-year') year = today.getFullYear() + 1;

      if (!record) {
        return {
          zip,
          mode,
          record: null,
          today,
          planDate,
          year,
          phase: 'unknown',
          daysRemaining: null,
          daysUntilLastFrost: null,
          seasonLength: null,
          lastFrost: null,
          firstFrost: null
        };
      }

      const lastFrost = dateFromMonthDay(record.last, year);
      const firstFrost = dateFromMonthDay(record.first, year);
      const allYear = record.last === '01-01' && record.first === '12-31';
      const seasonLength = Math.max(0, daysBetween(lastFrost, firstFrost));

      if (mode === 'next-year') planDate = addDays(lastFrost, 14);

      let phase = 'broad';
      let daysRemaining = null;
      let daysUntilLastFrost = null;
      if (allYear) {
        phase = 'all-year';
        if (planDate) daysRemaining = Math.max(0, daysBetween(planDate, firstFrost));
      } else if (mode === 'all-area' || mode === 'next-year') {
        phase = 'broad';
      } else if (planDate) {
        if (planDate < lastFrost) {
          phase = 'before-season';
          daysUntilLastFrost = Math.max(0, daysBetween(planDate, lastFrost));
        }
        else if (planDate > firstFrost) phase = 'after-season';
        else {
          phase = 'active';
          daysRemaining = Math.max(0, daysBetween(planDate, firstFrost));
        }
      }

      return {
        zip,
        mode,
        record,
        today,
        planDate,
        year,
        phase,
        allYear,
        daysRemaining,
        daysUntilLastFrost,
        seasonLength,
        lastFrost,
        firstFrost
      };
    }

    updateSeasonPreview() {
      if (!this.useSeasonTools || !this.seasonPreview) return;
      const zip = this.cleanZip(this.zipInput?.value);
      if (zip && zip.length !== 5) {
        this.seasonPreview.textContent = 'Keep entering the five-digit ZIP code. The seasonal estimate will appear here.';
        return;
      }

      const context = this.buildSeasonContext();
      if (!zip) {
        this.seasonPreview.textContent = 'Enter a ZIP code for an approximate seasonal check, or continue without one for broader recommendations.';
        return;
      }
      if (!context.record) {
        this.seasonPreview.textContent = 'This ZIP is not covered by the current regional lookup. You can still use every other part of the guide.';
        return;
      }

      const windowText = context.allYear
        ? 'The lookup treats this area as having little or no typical frost interruption.'
        : `The broad frost-free window is approximately ${formatDate(context.lastFrost)} through ${formatDate(context.firstFrost)}.`;

      let timingText = '';
      if (context.mode === 'next-year') {
        timingText = `Next year, the guide will keep the full season in view and use roughly ${formatDate(context.planDate, { year: true })} as a general warm-season planning point; cool-season and perennial timing can differ.`;
      } else if (context.mode === 'all-area') {
        timingText = 'The guide will compare varieties with the area’s full typical season instead of narrowing them to one date.';
      } else if (context.phase === 'active') {
        timingText = `${formatDate(context.planDate, { year: true })} leaves about ${context.daysRemaining} frost-free days before the average first fall frost.`;
      } else if (context.phase === 'before-season') {
        timingText = `${formatDate(context.planDate, { year: true })} is before the estimated average last spring frost, so frost-tender products may be better started later or protected.`;
      } else if (context.phase === 'after-season') {
        timingText = `${formatDate(context.planDate, { year: true })} is after the estimated average first fall frost, so the guide will also surface options to save for next season.`;
      } else if (context.phase === 'all-year') {
        timingText = 'The selected date falls within the broad year-round window used by this lookup.';
      }

      const maturity = this.maturityRecommendation(context);
      const maturityText = maturity.safeMax !== null
        ? `For frost-tender crops, start with listed maturity around ${maturity.safeMax} days or less; the finder will handle cool-season and perennial timing separately.`
        : maturity.heading;
      this.seasonPreview.innerHTML = `<strong>ZIP ${context.zip}:</strong> ${windowText} ${timingText}<span class="aonsk-finder__season-maturity"><strong>Maturity guide:</strong> ${maturityText}</span>`;
    }

    maturityRecommendation(season = this.buildSeasonContext()) {
      const querySeasonClass = cropSeasonClass(this.specificQuery);
      const seasonClass = querySeasonClass || this.config.collectionSeasonClass || 'mixed';
      const requestedCrop = this.specificQuery?.label || '';
      const cropPrefix = requestedCrop ? `For ${requestedCrop.toLowerCase()}, ` : '';
      const bufferDays = 14;
      const broadLabel = seasonClass === 'cool'
        ? (requestedCrop ? requestedCrop.toLowerCase() : 'cool-season varieties')
        : seasonClass === 'warm'
          ? (requestedCrop ? requestedCrop.toLowerCase() : 'frost-tender varieties')
          : 'frost-tender varieties';

      if (!season?.record) {
        return {
          safeMax: null,
          heading: 'Let the finder compare maturity automatically',
          text: 'Without a ZIP estimate, the guide will compare each product’s listed maturity and seasonal information without forcing a maturity range.'
        };
      }

      if (season.mode === 'next-year' || season.mode === 'all-area') {
        const safeMax = season.seasonLength ? Math.max(30, season.seasonLength - bufferDays) : null;
        return {
          safeMax,
          heading: season.mode === 'next-year' ? 'Planning for the full next season' : 'Using the area’s full typical season',
          text: safeMax
            ? `The guide can consider ${broadLabel} listed at roughly ${safeMax} days or less while leaving a basic two-week weather buffer. Cool-season, perennial, indoor-started, and protected crops may follow different timing.`
            : 'The guide will keep the area’s full growing season in view rather than forcing one maturity range.'
        };
      }

      if (season.phase === 'active') {
        const safeMax = Math.max(20, Number(season.daysRemaining || 0) - bufferDays);
        if (seasonClass === 'cool') {
          return {
            safeMax,
            heading: `${season.daysRemaining} estimated frost-free days remain`,
            text: `For this cool-season collection, temperature and frost tolerance matter as much as maturity. The guide will use the product’s season information automatically; ${safeMax} days is only a conservative ceiling for varieties that must finish before fall frost.`
          };
        }
        return {
          safeMax,
          heading: `${cropPrefix}start with about ${safeMax} days or less`,
          text: `${season.daysRemaining} estimated frost-free days remain. The guide will prioritize ${broadLabel} with listed maturity around ${safeMax} days or less, leaving a basic two-week buffer. Maturity may be counted from transplanting on some product pages.`
        };
      }

      if (season.phase === 'before-season') {
        if (seasonClass === 'cool') {
          return {
            safeMax: null,
            heading: 'Keep the automatic timing choice',
            text: 'This is a cool-season collection. The guide will weigh frost tolerance, planting season, and maturity together instead of treating the last-frost date as a simple countdown.'
          };
        }
        return {
          safeMax: null,
          heading: `Plan around the last frost near ${formatDate(season.lastFrost)}`,
          text: 'Keep “Use my planting window” selected. The guide will move frost-tender crops toward the post-frost window and keep earlier-start or protected-growing options separate.'
        };
      }

      if (season.phase === 'after-season') {
        return {
          safeMax: null,
          heading: 'Use the automatic seasonal comparison',
          text: 'The selected date is beyond the broad frost-free window. The guide will separate possible cool-season choices from products better saved for another season.'
        };
      }

      if (season.phase === 'all-year') {
        return {
          safeMax: null,
          heading: 'Use the automatic timing choice',
          text: 'This lookup treats the area as having little typical frost interruption, so heat, rainfall, and the individual product’s planting guidance matter more than a single maturity ceiling.'
        };
      }

      return {
        safeMax: null,
        heading: 'Let the finder compare maturity automatically',
        text: 'The guide will combine the selected date with each product’s maturity and seasonal information.'
      };
    }

    getState() {
      const categoryInput = this.selectedInput('category');
      const intentInput = this.selectedInput('intent');
      const locationInput = this.selectedInput('location');
      const sunInput = this.selectedInput('sun');
      const maturityInput = this.selectedInput('maturity');
      const availabilityInput = this.selectedInput('availability');
      const intent = this.config.intents?.find((item) => item.id === intentInput?.value) || null;

      const queryCategory = !categoryInput && (this.syntheticCategory || (this.specificQuery?.source === 'category' ? {
        value: this.specificQuery.value,
        label: this.specificQuery.categoryLabel || this.specificQuery.label || '',
        param: this.specificQuery.param || '',
        count: Number(this.specificQuery.count || 0),
        source: 'note'
      } : null));
      const maturityIsAuto = Boolean(maturityInput?.hasAttribute('data-maturity-auto'));
      const experienceCriteria = Array.from(this.querySelectorAll('[data-finder-experience][aria-pressed="true"]')).map((button) => ({
        id: button.dataset.finderExperience || '',
        label: button.dataset.finderExperienceLabel || button.textContent.trim()
      })).filter((item) => item.id);

      return {
        category: categoryInput ? {
          value: categoryInput.value,
          label: categoryInput.dataset.filterLabel || '',
          param: categoryInput.dataset.filterParam || '',
          source: categoryInput.dataset.selectedByNote === 'true' ? 'note' : 'choice'
        } : queryCategory,
        intent,
        query: this.specificQuery,
        naturalQuery: this.naturalQuery,
        experienceCriteria,
        location: locationInput ? {
          value: locationInput.value,
          label: locationInput.dataset.filterLabel || '',
          param: locationInput.dataset.filterParam || ''
        } : null,
        sun: sunInput ? {
          value: sunInput.value,
          label: sunInput.dataset.filterLabel || '',
          param: sunInput.dataset.filterParam || ''
        } : null,
        maturity: maturityInput && !maturityIsAuto ? {
          value: maturityInput.value,
          label: maturityInput.dataset.filterLabel || '',
          param: maturityInput.dataset.filterParam || ''
        } : null,
        maturityMode: maturityIsAuto ? 'automatic' : (maturityInput ? 'manual' : 'broad'),
        availability: availabilityInput ? {
          value: availabilityInput.value,
          label: availabilityInput.dataset.filterLabel || '',
          param: availabilityInput.dataset.filterParam || '',
          mode: availabilityInput.dataset.availability || 'all'
        } : { mode: 'all' },
        season: this.buildSeasonContext()
      };
    }

    applyNaturalNote(rawNote) {
      const note = normalizeText(rawNote).trim();
      this.naturalQuery = note;
      this.syntheticCategory = null;
      this.specificQuery = note ? this.findSpecificQuery(note, true) : null;
      this.updateQueryRecognition();
      if (!note) return;

      let bestIntent = null;
      let bestIntentScore = 0;
      (this.config.intents || []).forEach((intent) => {
        const keywords = (intent.keywords || []).map((keyword) => normalizeText(keyword).trim()).filter(Boolean);
        let score = 0;
        keywords.forEach((keyword) => {
          if (containsPhrase(note, keyword)) score += keyword.length > 7 ? 3 : 2;
        });
        if (intent.specialMode === 'container' && /container|pot|porch|patio|balcony|small space/.test(note)) score += 2;
        if (score > bestIntentScore) {
          bestIntentScore = score;
          bestIntent = intent;
        }
      });
      if (bestIntent) this.checkRadio(`input[name^="finder-intent-"][value="${CSS.escape(bestIntent.id)}"]`);

      const locationInputs = Array.from(this.querySelectorAll('[data-finder-step="location"] input'));
      const locationSynonyms = [
        { pattern: /container|pot|porch|patio|balcony/, values: ['container'] },
        { pattern: /raised bed/, values: ['raised bed'] },
        { pattern: /in[ -]?ground|field|garden bed/, values: ['in-ground'] },
        { pattern: /greenhouse/, values: ['greenhouse'] },
        { pattern: /high tunnel/, values: ['high tunnel'] },
        { pattern: /hydroponic/, values: ['hydroponic'] },
        { pattern: /indoor|windowsill|inside/, values: ['indoor'] },
        { pattern: /vertical|tower/, values: ['vertical'] },
        { pattern: /community garden/, values: ['community garden'] }
      ];
      for (const group of locationSynonyms) {
        if (!group.pattern.test(note)) continue;
        const input = locationInputs.find((candidate) => {
          const label = normalizeText(candidate.dataset.filterLabel);
          return group.values.some((value) => label.includes(value));
        });
        if (input) {
          this.checkElement(input);
          break;
        }
      }

      ['sun'].forEach((stepName) => {
        const inputs = Array.from(this.querySelectorAll(`[data-finder-step="${stepName}"] input[data-filter-label]`));
        const exact = inputs.find((candidate) => {
          const label = normalizeText(candidate.dataset.filterLabel).trim();
          return label.length > 2 && containsPhrase(note, label);
        });
        if (exact) this.checkElement(exact);
      });

      const sunInputs = Array.from(this.querySelectorAll('[data-finder-step="sun"] input'));
      const sunGroups = [
        { pattern: /full sun|sunny|lots of sun/, values: ['full sun', 'sun'] },
        { pattern: /part sun|partial sun|morning sun/, values: ['part sun', 'partial sun'] },
        { pattern: /shade|shady|low light/, values: ['shade', 'partial shade'] }
      ];
      for (const group of sunGroups) {
        if (!group.pattern.test(note)) continue;
        const input = sunInputs.find((candidate) => {
          const label = normalizeText(candidate.dataset.filterLabel);
          return group.values.some((value) => label.includes(value));
        });
        if (input) {
          this.checkElement(input);
          break;
        }
      }

      const zipMatch = note.match(/\b(\d{5})(?:-\d{4})?\b/);
      if (zipMatch && this.zipInput) this.zipInput.value = zipMatch[1];

      if (this.planSelect) {
        if (/next year|next season|for spring next/.test(note)) this.planSelect.value = 'next-year';
        else if (/all options|all varieties|any time|not planting yet|just exploring/.test(note)) this.planSelect.value = 'all-area';
        else if (/plant(?:ing)? now|right now|this week|today|still plant/.test(note)) this.planSelect.value = 'now';
      }

      const maturityInputs = Array.from(this.querySelectorAll('[data-finder-step="maturity"] input[data-maturity-manual]'));
      const dayMatch = note.match(/(?:about|under|within|around)?\s*(\d{2,3})\s*days?/);
      if (dayMatch) {
        const desired = Number(dayMatch[1]);
        const input = maturityInputs.find((candidate) => {
          const range = parseRange(candidate.dataset.filterLabel);
          return range.min !== null && desired >= range.min && desired <= range.max;
        });
        if (input) this.checkElement(input);
      } else if (/fast|quick|soon|early|short season/.test(note)) {
        // “Fast” is a preference, not a request for an arbitrary fixed range.
        // Keep the ZIP/date-driven automatic option selected and let the faster-results
        // intent rank shorter varieties inside the actual planting window.
        const automatic = this.querySelector('[data-maturity-auto]');
        if (automatic) this.checkElement(automatic);
      }

      if (/in stock|available now|ship now|order now/.test(note)) {
        const inStock = this.querySelector('[data-availability="in-stock"]');
        if (inStock) this.checkElement(inStock);
      }

      this.savePlanningContext();
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateMaturityGuidance();
      this.updateDialogContext();
    }

    findSpecificQuery(rawNote, selectCategory = false) {
      const note = normalizeForMatch(rawNote);
      if (!note) return null;

      const visibleInputs = Array.from(this.querySelectorAll('[data-finder-step="category"] input[data-filter-label]'));
      const options = [];
      const seen = new Set();
      const addOption = (option, input = null) => {
        const label = option?.label || input?.dataset.filterLabel || input?.value || '';
        const value = option?.value ?? input?.value ?? '';
        const param = option?.param || input?.dataset.filterParam || '';
        const key = `${param}::${value}::${label}`;
        if (!label || seen.has(key)) return;
        seen.add(key);
        options.push({ label, value, param, count: Number(option?.count || 0), input });
      };

      (this.config.categoryOptions || []).forEach((option) => {
        const input = visibleInputs.find((candidate) => candidate.value === String(option.value)
          || normalizeForMatch(candidate.dataset.filterLabel) === normalizeForMatch(option.label));
        addOption(option, input || null);
      });
      visibleInputs.forEach((input) => addOption(null, input));

      const cropRule = detectCropRule(note);
      let best = null;
      let secondBest = null;
      options.forEach((option) => {
        const parts = categoryParts(option.label);
        if (!parts.tokens.length) return;
        let score = 0;
        let matchedTerm = '';

        if (parts.normalized.length > 3 && containsPhrase(note, parts.normalized)) {
          score = 220 + parts.normalized.length;
          matchedTerm = parts.core || parts.normalized;
        }
        if (parts.core.length > 3 && containsPhrase(note, parts.core)) {
          const coreScore = 190 + parts.core.length;
          if (coreScore > score) {
            score = coreScore;
            matchedTerm = parts.core;
          }
        }

        parts.tokens.forEach((token) => {
          if (token.length < 3 || !containsPhrase(note, token)) return;
          const tokenScore = 70 + token.length - Math.max(0, parts.tokens.length - 1) * 3;
          if (tokenScore > score) {
            score = tokenScore;
            matchedTerm = token;
          }
        });

        if (cropRule) {
          cropRule.aliases.forEach((alias) => {
            if (!containsPhrase(parts.normalized, alias)) return;
            const aliasTokens = categoryParts(alias).tokens;
            const extraTokens = Math.max(0, parts.tokens.length - aliasTokens.length);
            const aliasScore = 150 + alias.length - (extraTokens * 5);
            if (aliasScore > score) {
              score = aliasScore;
              matchedTerm = alias;
            }
          });
        }

        if (!score) return;
        const candidate = {
          score,
          term: matchedTerm || parts.core || parts.normalized,
          label: cropRule && cropRule.aliases.some((alias) => containsPhrase(parts.normalized, alias))
            ? titleCase(cropRule.label)
            : titleCase(matchedTerm || parts.core || option.label),
          categoryLabel: option.label,
          value: option.value,
          param: option.param,
          count: option.count,
          input: option.input,
          strict: true,
          source: 'category'
        };
        if (!best || candidate.score > best.score) {
          secondBest = best;
          best = candidate;
        } else if (!secondBest || candidate.score > secondBest.score) {
          secondBest = candidate;
        }
      });

      // A generic crop word such as “bean” can match several subtypes. When the
      // category match is ambiguous, keep it as a strict text request instead of
      // silently choosing one subtype for the shopper.
      if (best && secondBest && best.score - secondBest.score < 4 && best.score < 180) {
        best = null;
      }

      if (!best && cropRule) {
        best = {
          score: 60 + cropRule.id.length,
          term: cropRule.id,
          label: titleCase(cropRule.label),
          strict: true,
          source: 'text'
        };
      }

      if (!best) {
        FALLBACK_CROP_TERMS.forEach(([term, label]) => {
          if (!containsPhrase(note, term)) return;
          const score = 50 + term.length;
          if (score > (best?.score || 0)) best = { score, term, label, strict: true, source: 'text' };
        });
      }

      if (best?.input && selectCategory) {
        this.querySelectorAll('[data-finder-step="category"] input[data-selected-by-note="true"]').forEach((input) => {
          if (input !== best.input) {
            input.checked = false;
            delete input.dataset.selectedByNote;
          }
        });
        best.input.dataset.selectedByNote = 'true';
        this.checkElement(best.input);
      }

      if (!best) return null;
      const { input, score, ...query } = best;
      return query;
    }

    updateQueryRecognition() {
      if (!this.queryRecognition) return;
      const note = this.combinedNaturalNote();
      const query = note ? this.findSpecificQuery(note, false) : null;
      this.queryRecognition.replaceChildren();
      if (!query) {
        this.queryRecognition.hidden = true;
        return;
      }
      const strong = document.createElement('strong');
      strong.textContent = `Recognized crop: ${query.label}`;
      const span = document.createElement('span');
      span.textContent = ' The finder will keep the shortlist inside this crop instead of mixing in unrelated products.';
      this.queryRecognition.append(strong, span);
      this.queryRecognition.hidden = false;
    }

    clearMaturityGuidance() {
      this.recommendedMaturityInput = null;
      this.querySelectorAll('[data-finder-step="maturity"] .aonsk-finder__choice').forEach((choice) => {
        choice.classList.remove('is-season-recommended', 'is-outside-window');
        choice.querySelector('[data-maturity-recommended-badge]')?.remove();
      });
    }

    updateMaturityGuidance() {
      if (!this.maturityContext) return;
      const manualInputs = Array.from(this.querySelectorAll('[data-finder-step="maturity"] input[data-maturity-manual]'));
      const autoInput = this.querySelector('[data-maturity-auto]');
      this.clearMaturityGuidance();
      if (!manualInputs.length && !autoInput) {
        this.maturityContext.hidden = true;
        return;
      }

      const recommendation = this.maturityRecommendation();
      const selected = this.selectedInput('maturity');
      const selectedIsManual = Boolean(selected?.hasAttribute('data-maturity-manual'));
      const selectedRange = selectedIsManual ? parseRange(selected.dataset.filterLabel) : { min: null, max: null };
      const outsideWindow = Boolean(
        selectedIsManual
        && recommendation.safeMax !== null
        && selectedRange.min !== null
        && selectedRange.min > recommendation.safeMax
      );

      this.maturityContext.hidden = false;
      this.maturityContext.classList.toggle('is-warning', outsideWindow);
      if (this.maturityContextHeading) {
        this.maturityContextHeading.textContent = outsideWindow
          ? `${selected.dataset.filterLabel} is longer than the current timing target`
          : recommendation.heading;
      }
      if (this.maturityContextCopy) {
        this.maturityContextCopy.textContent = outsideWindow
          ? `The ZIP and planting date point toward roughly ${recommendation.safeMax} days or less for frost-tender crops. Keep this longer range only for an earlier indoor start, protected growing, or another season.`
          : `${recommendation.text} You do not have to choose a maturity range; “Use my ZIP and planting date” lets the finder compare every product automatically.`;
      }
      if (this.maturityAutoCopy) {
        this.maturityAutoCopy.textContent = recommendation.safeMax !== null
          ? `Recommended. The finder will favor products listed around ${recommendation.safeMax} days or less where that seasonal limit applies.`
          : 'Recommended. The finder will compare maturity, frost timing, and season information without forcing one range.';
      }

      const choices = manualInputs.map((input) => ({
        input,
        range: parseRange(input.dataset.filterLabel),
        label: input.dataset.filterLabel || input.value
      })).filter((item) => item.range.min !== null)
        .sort((a, b) => (a.range.min - b.range.min) || (a.range.max - b.range.max));

      let recommended = null;
      if (recommendation.safeMax !== null) {
        recommended = choices.find((item) => recommendation.safeMax >= item.range.min && recommendation.safeMax <= item.range.max)
          || choices.filter((item) => item.range.max <= recommendation.safeMax).sort((a, b) => b.range.max - a.range.max)[0]
          || choices[0]
          || null;
      }

      manualInputs.forEach((input) => {
        const choice = input.closest('.aonsk-finder__choice');
        const help = choice?.querySelector('small');
        if (!choice || !help) return;
        if (!help.dataset.baseText) help.dataset.baseText = help.textContent;
        const range = parseRange(input.dataset.filterLabel);
        const tooLong = recommendation.safeMax !== null && range.min !== null && range.min > recommendation.safeMax;
        choice.classList.toggle('is-outside-window', tooLong);
        help.textContent = tooLong
          ? `${help.dataset.baseText} · Longer than the approximate ${recommendation.safeMax}-day target`
          : help.dataset.baseText;
      });

      if (recommended) {
        this.recommendedMaturityInput = recommended.input;
        const choice = recommended.input.closest('.aonsk-finder__choice');
        choice?.classList.add('is-season-recommended');
        const labelWrap = choice?.querySelector('span:last-child');
        if (labelWrap && !labelWrap.querySelector('[data-maturity-recommended-badge]')) {
          const badge = document.createElement('em');
          badge.dataset.maturityRecommendedBadge = 'true';
          badge.className = 'aonsk-finder__maturity-badge';
          badge.textContent = 'Closest manual range';
          labelWrap.append(badge);
        }
      }

      if (this.maturityUseButton) {
        this.maturityUseButton.hidden = !recommended || !selectedIsManual;
        this.maturityUseButton.textContent = recommended ? `Narrow to ${recommended.label}` : 'Use suggested range';
      }
    }

    syncChoiceStates() {
      this.querySelectorAll('.aonsk-finder__choice').forEach((choice) => {
        choice.classList.toggle('is-selected', Boolean(choice.querySelector('input')?.checked));
      });
    }

    checkRadio(selector) {
      const input = this.querySelector(selector);
      if (input) this.checkElement(input);
    }

    checkElement(input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    productText(product) {
      return normalizeText([
        product.title,
        product.tags,
        product.type,
        product.category,
        product.locations,
        product.uses,
        product.flavor,
        product.experienceLabel,
        product.germinationDifficulty,
        product.germinationPreparation,
        product.stratification,
        product.scarification,
        product.soaking,
        product.lightRequirement,
        product.germinationReliability,
        product.growingComplexity,
        product.transplantSensitivity,
        product.firstHarvestOrBloom,
        product.seedSuccessTip,
        product.germinationText,
        product.growthHabit,
        product.sun,
        product.plantingSeason,
        product.frostHardy,
        product.lifeCycle,
        product.coldTolerance,
        product.heatTolerance,
        product.highlight,
        product.benefits,
        product.description
      ]);
    }

    productMatchesQuery(product, query) {
      if (!query?.term) return true;
      const text = this.productText(product);
      if (containsPhrase(text, query.term)) return true;
      const rule = CROP_RULES.find((item) => item.id === query.term || item.aliases.some((alias) => containsPhrase(query.term, alias)));
      return Boolean(rule && rule.aliases.some((alias) => containsPhrase(text, alias)));
    }

    productCropGroup(product) {
      const rule = detectCropRule(this.productText(product));
      if (rule) return rule.id;
      const type = normalizeForMatch(product.type || product.category);
      return type || 'other';
    }

    productIdentityText(product) {
      return normalizeText([
        product.title,
        product.tags,
        product.type,
        product.category
      ]);
    }

    inferCatalogSeasonClass(text, lifeCycle = '', fallback = this.config.collectionSeasonClass || 'mixed') {
      const normalized = normalizeForMatch(text);
      const normalizedLifeCycle = normalizeForMatch(lifeCycle);

      // Customer-facing crop identity takes priority over botanical life-cycle
      // wording. Many familiar vegetables are biennial, and a few can behave as
      // short-lived perennials in mild climates, but shoppers still need ordinary
      // harvest-window guidance for collards, cabbage, carrots, onions, beans,
      // tomatoes, and the other crops below.
      if (/\b(lettuce|spinach|kale|collards?|broccoli|cauliflower|cabbage|brussels sprouts?|kohlrabi|radish|turnip|rutabaga|beet|carrot|parsnip|pea|arugula|chard|mustard|bok choy|pak choi|tatsoi|mizuna|komatsuna|mache|celery|celeriac|cilantro|parsley|dill|endive|chicory|onion|leek|garlic|fava)\b/.test(normalized)) return 'cool';
      if (/\b(tomato|tomatillo|ground cherry|pepper|chile|eggplant|okra|corn|bean|cowpea|squash|pumpkin|cucumber|melon|watermelon|basil|amaranth|sunflower|zinnia|cosmos|celosia|gourd|peanut|roselle|sweet potato)\b/.test(normalized)) return 'warm';

      const isExplicitPerennial = /\bperennial\b/.test(normalizedLifeCycle)
        && !/\bannual\b/.test(normalizedLifeCycle)
        && !/\bbiennial\b/.test(normalizedLifeCycle);
      if (isExplicitPerennial) return 'perennial';

      // Only treat identity text as perennial when it says perennial without
      // also describing the plant as annual or biennial. Product descriptions
      // are intentionally excluded because they often mention perennial gardens,
      // perennial relatives, or general landscaping advice.
      if (/\bperennial\b/.test(normalized)
        && !/\bannual\b/.test(normalized)
        && !/\bbiennial\b/.test(normalized)) return 'perennial';

      return fallback || 'mixed';
    }

    productSeasonClass(product) {
      return this.inferCatalogSeasonClass(
        this.productIdentityText(product),
        product.lifeCycle,
        product.seasonClass || this.config.collectionSeasonClass || 'mixed'
      );
    }

    extractCatalogMaturity(text) {
      const normalized = stripHtml(text);
      const patterns = [
        /(?:days?\s+to\s+maturity|maturity|days?\s+to\s+harvest|harvest\s+in)\D{0,24}(\d{2,3})(?:\s*(?:-|–|—|to)\s*(\d{2,3}))?\s*days?/i,
        /(\d{2,3})\s*(?:-|–|—|to)\s*(\d{2,3})\s*days?\b/i,
        /\b(\d{2,3})\s*days?\s*(?:to\s+maturity|to\s+harvest|until\s+harvest)/i
      ];
      for (const pattern of patterns) {
        const match = normalized.match(pattern);
        if (!match) continue;
        return match[2] ? `${match[1]}-${match[2]} days` : `${match[1]} days`;
      }
      return '';
    }

    formatCatalogPrice(value) {
      const number = Number.parseFloat(value);
      if (!Number.isFinite(number)) return '';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.config.currency || 'USD'
        }).format(number);
      } catch (error) {
        return `$${number.toFixed(2)}`;
      }
    }

    mapCatalogProduct(raw) {
      const variants = Array.isArray(raw?.variants) ? raw.variants : [];
      const availableVariant = variants.find((variant) => variant.available) || variants[0] || {};
      const images = Array.isArray(raw?.images) ? raw.images : [];
      const firstImage = images[0];
      const image = typeof firstImage === 'string'
        ? firstImage
        : (firstImage?.src || firstImage?.url || raw?.featured_image?.url || raw?.featured_image?.src || raw?.image?.src || raw?.image?.url || raw?.image || '');
      const description = stripHtml(raw?.body_html || raw?.body || raw?.description || '');
      const tags = Array.isArray(raw?.tags) ? raw.tags : String(raw?.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
      const identityText = [raw?.title, raw?.product_type, tags.join(' ')].join(' ');
      const combined = [identityText, description].join(' ');
      return {
        id: raw?.id,
        title: raw?.title || 'Seed variety',
        url: raw?.url || (raw?.handle ? `/products/${raw.handle}` : '#'),
        image,
        imageAlt: raw?.title || 'Seed variety',
        available: variants.some((variant) => variant.available) || Boolean(raw?.available),
        price: this.formatCatalogPrice(availableVariant.price || raw?.price || 0),
        tags,
        type: raw?.product_type || raw?.type || '',
        category: raw?.product_type || raw?.type || '',
        locations: '',
        uses: '',
        flavor: '',
        maturityMin: null,
        maturityMax: null,
        maturityText: this.extractCatalogMaturity(description),
        germinationText: '',
        experienceLabel: '',
        experienceRank: null,
        germinationDifficulty: '',
        germinationRank: null,
        germinationPreparation: '',
        stratification: '',
        scarification: '',
        soaking: '',
        lightRequirement: '',
        germinationReliability: '',
        growingComplexity: '',
        transplantSensitivity: '',
        firstHarvestOrBloom: '',
        seedSuccessTip: '',
        containerSuitable: /\b(container|patio|compact|dwarf)\b/i.test(combined),
        growthHabit: '',
        sun: '',
        lifeCycle: '',
        plantingSeason: '',
        coldTolerance: '',
        heatTolerance: '',
        frostHardy: '',
        highlight: '',
        benefits: '',
        description,
        seasonClass: this.inferCatalogSeasonClass(identityText)
      };
    }

    async fetchFullCatalog() {
      if (Array.isArray(this.catalogProducts)) return this.catalogProducts;
      if (this.catalogPromise) return this.catalogPromise;
      const total = Math.max(0, Number(this.config.collectionProductCount || 0));
      const pageCount = Math.max(1, Math.min(6, Math.ceil(total / 250)));
      const baseOrigin = window.location.origin && window.location.origin !== 'null' ? window.location.origin : 'https://aonsk.local';
      const collectionUrl = new URL(this.config.collectionUrl || window.location.pathname, baseOrigin);
      const endpoint = `${collectionUrl.pathname.replace(/\/$/, '')}/products.json`;

      this.catalogPromise = Promise.all(Array.from({ length: pageCount }, async (_, index) => {
        const url = new URL(endpoint, baseOrigin);
        url.searchParams.set('limit', '250');
        url.searchParams.set('page', String(index + 1));
        const response = await fetch(url.pathname + url.search, { credentials: 'same-origin', cache: 'force-cache' });
        if (!response.ok) throw new Error(`Collection catalog request failed with ${response.status}`);
        const payload = await response.json();
        return Array.isArray(payload?.products) ? payload.products : [];
      })).then((pages) => {
        const mapped = pages.flat().map((product) => this.mapCatalogProduct(product));
        const seen = new Set();
        this.catalogProducts = mapped.filter((product) => {
          const key = String(product.id || product.url || product.title);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        this.catalogFetchFailed = false;
        return this.catalogProducts;
      }).catch((error) => {
        console.warn('The full collection catalog could not be loaded for guided recommendations.', error);
        this.catalogFetchFailed = true;
        this.catalogProducts = [];
        return [];
      }).finally(() => {
        this.catalogPromise = null;
      });
      return this.catalogPromise;
    }

    async fetchSearchMatches(query) {
      const term = normalizeForMatch(query);
      if (!term) return [];
      if (this.searchCache.has(term)) return this.searchCache.get(term);

      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title,product_type,tag,body'
      });

      try {
        const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Predictive search request failed with ${response.status}`);
        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        const mapped = products.map((product) => this.mapCatalogProduct(product));
        this.searchCache.set(term, mapped);
        return mapped;
      } catch (error) {
        console.warn('The store search fallback could not load guided matches.', error);
        this.searchCache.set(term, []);
        return [];
      }
    }

    async productsForState(state) {
      const detailed = Array.isArray(this.config.products) ? this.config.products : [];
      const total = Number(this.config.collectionProductCount || detailed.length);
      const hasNarrowing = Boolean(state.query || state.category || state.intent || state.location || state.sun || state.maturity || state.experienceCriteria?.length);
      // Once a shopper asks the finder to narrow a collection, evaluate the full
      // collection rather than only the first 50 server-rendered products. Broad
      // catalog pages still use crop diversity, while specific crop pages retain
      // every relevant subtype and use within that collection.
      if (total <= detailed.length || !hasNarrowing) return detailed;

      const catalog = await this.fetchFullCatalog();
      const merged = new Map();
      catalog.forEach((product) => merged.set(String(product.id || product.url), product));
      detailed.forEach((product) => merged.set(String(product.id || product.url), product));

      let products = Array.from(merged.values());
      const needsSearchFallback = state.query?.source === 'category'
        && state.query?.term
        && !products.some((product) => this.productMatchesQuery(product, state.query));
      if (needsSearchFallback) {
        const searchMatches = await this.fetchSearchMatches(state.query.term);
        searchMatches.forEach((product) => merged.set(String(product.id || product.url), product));
        products = Array.from(merged.values());
      }

      return products.length ? products : detailed;
    }

    selectDiverse(items, max, state) {
      if (state.query || state.category || !this.isBroadCollection()) return items.slice(0, max);
      const selected = [];
      const counts = new Map();

      // First show the strongest product from as many crop groups as possible.
      items.forEach((item) => {
        if (selected.length >= max) return;
        const group = this.productCropGroup(item.product);
        if ((counts.get(group) || 0) > 0) return;
        counts.set(group, 1);
        selected.push(item);
      });

      // If room remains, allow a second strong product from a crop group. Do not
      // fill a broad vegetable shortlist with six nearly identical bean results.
      if (selected.length < max) {
        items.forEach((item) => {
          if (selected.length >= max || selected.includes(item)) return;
          const group = this.productCropGroup(item.product);
          const count = counts.get(group) || 0;
          if (count >= 2) return;
          counts.set(group, count + 1);
          selected.push(item);
        });
      }
      return selected;
    }

    showLoading(state) {
      if (!this.results || !this.resultGrid) return;
      this.results.hidden = false;
      this.resultGrid.replaceChildren();
      if (this.laterGrid) this.laterGrid.replaceChildren();
      if (this.laterWrap) this.laterWrap.hidden = true;
      if (this.resultsHeading) this.resultsHeading.textContent = state.query?.label
        ? `Looking through the full collection for ${state.query.label.toLowerCase()}…`
        : 'Looking through the collection…';
      if (this.resultsSummary) this.resultsSummary.textContent = 'The finder is checking the broader catalog and your planting-window choices. This stays on the store and does not use Artificial Intelligence.';
      const loading = document.createElement('div');
      loading.className = 'aonsk-finder__loading';
      loading.setAttribute('role', 'status');
      loading.innerHTML = '<span aria-hidden="true"></span><strong>Finding useful matches…</strong>';
      this.resultGrid.append(loading);
      requestAnimationFrame(() => this.results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' }));
    }

    productMaturity(product) {
      const textRange = parseRange(product.maturityText);
      const min = numberFrom(product.maturityMin) ?? textRange.min;
      const max = numberFrom(product.maturityMax) ?? textRange.max ?? min;
      return { min, max };
    }

    assessSeasonFit(product, season) {
      const maturity = this.productMaturity(product);
      const neutral = {
        score: 0,
        exact: true,
        group: 'primary',
        label: '',
        className: '',
        reason: ''
      };

      if (!this.useSeasonTools || !season?.record) return neutral;

      const productSeason = this.productSeasonClass(product);
      const frostText = normalizeText(product.frostHardy);
      const frostHardy = productSeason === 'cool' || /hardy|tolerant|yes|light frost|moderate frost/.test(frostText);
      const seasonLength = season.seasonLength;
      const matureWithinFullSeason = maturity.min === null || seasonLength === null || maturity.min + 7 <= seasonLength;

      if (productSeason === 'perennial') {
        return {
          score: 1,
          exact: false,
          group: 'primary',
          label: 'Check perennial establishment',
          className: 'is-broad',
          reason: 'This product is explicitly identified as perennial, so establishment and winter hardiness depend on its individual growing guidance rather than maturity alone'
        };
      }

      if (season.mode === 'all-area') {
        return {
          score: matureWithinFullSeason ? 3 : -1,
          exact: matureWithinFullSeason,
          group: 'primary',
          label: 'Area-wide season view',
          className: 'is-broad',
          reason: matureWithinFullSeason
            ? 'Its listed timing fits within the area’s broad typical growing window'
            : 'It may need an earlier start, protected growing, or another seasonal window'
        };
      }

      if (season.mode === 'next-year') {
        let reason = 'Can be considered within next year’s broad growing season';
        if (productSeason === 'warm') reason = `Consider it after the broad last-frost estimate around ${formatDate(season.lastFrost)}`;
        if (productSeason === 'cool') reason = 'Consider spring or fall timing and compare the product’s frost guidance';
        return {
          score: matureWithinFullSeason ? 4 : 0,
          exact: matureWithinFullSeason,
          group: 'primary',
          label: 'Next-season plan',
          className: 'is-next',
          reason: matureWithinFullSeason ? reason : 'It may need an earlier indoor start, protected conditions, or a longer season'
        };
      }

      if (season.phase === 'all-year') {
        return {
          score: 5,
          exact: true,
          group: 'primary',
          label: 'Season window fits',
          className: 'is-strong',
          reason: 'The selected date fits the broad year-round window used for this ZIP'
        };
      }

      if (season.phase === 'before-season') {
        if (productSeason === 'warm') {
          return {
            score: -4,
            exact: false,
            group: 'later',
            label: 'Better after frost',
            className: 'is-later',
            reason: `Warm-season products are generally better after the estimated last frost around ${formatDate(season.lastFrost)}`
          };
        }

        if (productSeason === 'cool') {
          const availableDays = season.daysUntilLastFrost;
          const conservativeDays = maturity.min === null ? null : (maturity.max ?? maturity.min) + 7;
          const likelyFits = conservativeDays !== null && availableDays !== null && conservativeDays <= availableDays;
          return {
            score: likelyFits ? 7 : 3,
            exact: likelyFits,
            group: 'primary',
            label: likelyFits ? 'Cool-season window may fit' : 'Check cool-season timing',
            className: likelyFits ? 'is-strong' : 'is-possible',
            reason: likelyFits
              ? `Its listed maturity may fit before the broad last-frost estimate in about ${availableDays} days`
              : 'Cool-season products can sometimes be planted before the main frost-free window; confirm the variety’s frost guidance'
          };
        }

        return {
          score: 0,
          exact: false,
          group: 'primary',
          label: 'Check frost guidance',
          className: 'is-possible',
          reason: `The selected date is before the broad last-frost estimate around ${formatDate(season.lastFrost)}`
        };
      }

      if (season.phase === 'after-season') {
        if (productSeason === 'cool' && frostHardy) {
          return {
            score: 2,
            exact: false,
            group: 'primary',
            label: 'Possible cool-season option',
            className: 'is-possible',
            reason: 'This appears to be a cool-season or frost-tolerant product, but local winter conditions still matter'
          };
        }
        return {
          score: -6,
          exact: false,
          group: 'later',
          label: 'Save for another window',
          className: 'is-later',
          reason: 'The selected date falls after the broad average first-frost window'
        };
      }

      if (season.phase === 'active') {
        if (maturity.min === null) {
          return {
            score: 0,
            exact: false,
            group: 'primary',
            label: 'Check timing details',
            className: 'is-possible',
            reason: `${season.daysRemaining} estimated frost-free days remain, but this product needs clearer timing data`
          };
        }

        const conservativeDays = (maturity.max ?? maturity.min) + 7;
        const minimumDays = maturity.min + 7;
        if (conservativeDays <= season.daysRemaining) {
          return {
            score: 8,
            exact: true,
            group: 'primary',
            label: 'Good planting-window fit',
            className: 'is-strong',
            reason: productSeason === 'cool'
              ? `Its listed maturity fits within about ${season.daysRemaining} days, though summer heat can still affect cool-season crops`
              : `Its listed maturity fits comfortably within about ${season.daysRemaining} frost-free days`
          };
        }
        if (minimumDays <= season.daysRemaining) {
          return {
            score: 3,
            exact: false,
            group: 'primary',
            label: 'Possible this season',
            className: 'is-possible',
            reason: `The earlier end of its timing range may fit within about ${season.daysRemaining} frost-free days`
          };
        }
        return {
          score: -5,
          exact: false,
          group: 'later',
          label: 'Better for another window',
          className: 'is-later',
          reason: `Its listed timing is longer than the roughly ${season.daysRemaining} frost-free days remaining`
        };
      }

      return neutral;
    }

    scoreProduct(product, state) {
      const text = this.productText(product);
      const reasons = [];
      let score = product.available ? 1 : 0;
      let exact = true;

      if (state.availability?.mode === 'in-stock' && !product.available) {
        return { product, score: -999, reasons, exact: false, excluded: true, season: null };
      }

      if (state.experienceCriteria?.length) {
        const experienceRank = Number(product.experienceRank || 0);
        const germinationRank = Number(product.germinationRank || 0);
        const preparationText = normalizeText([product.germinationPreparation, product.stratification, product.scarification, product.soaking, product.lightRequirement]);
        const firstTiming = normalizeText(product.firstHarvestOrBloom);
        const tagText = normalizeText(product.tags);
        const lifeCycle = normalizeText(product.lifeCycle);
        const hasReviewedProfile = experienceRank > 0 || germinationRank > 0 || Boolean(preparationText || product.germinationReliability || product.growingComplexity || product.transplantSensitivity || firstTiming);
        if (!hasReviewedProfile) {
          return { product, score: -999, reasons, exact: false, excluded: true, season: null, profileMissing: true };
        }
        const specialPrep = Boolean(preparationText) && !/(no special|none required|not required|standard sowing|either light or dark)/.test(preparationText);
        const germinationRange = parseRange(product.germinationText);
        const germinationDays = germinationRange.max ?? germinationRange.min;
        for (const criterion of state.experienceCriteria) {
          let matches = false;
          if (criterion.id === 'beginner') matches = experienceRank > 0 && experienceRank <= 2;
          else if (criterion.id === 'no-prep') matches = !specialPrep && (experienceRank > 0 || germinationRank > 0);
          else if (criterion.id === 'fast') matches = (germinationRank > 0 && germinationRank <= 2) || (germinationDays !== null && germinationDays <= 14);
          else if (criterion.id === 'direct') matches = /direct sow/.test(tagText) || /direct sow/.test(normalizeText([product.growingComplexity, product.seedSuccessTip]));
          else if (criterion.id === 'patient') matches = experienceRank >= 4 || germinationRank >= 4;
          else if (criterion.id === 'advanced') matches = experienceRank >= 5;
          else if (criterion.id === 'first-year') matches = /first season|first year/.test(firstTiming);
          else if (criterion.id === 'perennial') matches = /perennial/.test(lifeCycle) || /native seeds|native plant/.test(tagText);
          if (!matches) return { product, score: -999, reasons, exact: false, excluded: true, season: null };
          score += 12;
          reasons.push(`Matches ${criterion.label.toLowerCase()}`);
        }
      }

      if (state.category) {
        const parts = categoryParts(state.category.label);
        const categoryText = normalizeText([product.type, product.category, product.title, product.tags]);
        const matchesCategory = containsPhrase(categoryText, parts.normalized)
          || (parts.core && containsPhrase(categoryText, parts.core))
          || parts.tokens.some((token) => token.length >= 4 && containsPhrase(categoryText, token));
        if (matchesCategory) {
          score += 32;
          reasons.push(`Matches ${state.category.label.toLowerCase()}`);
        } else {
          return { product, score: -999, reasons, exact: false, excluded: true, season: null, queryMatched: false };
        }
      }

      let queryMatched = !state.query;
      if (state.query?.term && !state.category) {
        queryMatched = this.productMatchesQuery(product, state.query);
        if (!queryMatched && state.query.strict) {
          return { product, score: -999, reasons, exact: false, excluded: true, season: null, queryMatched: false };
        }
        if (queryMatched) {
          const titleText = normalizeText(product.title);
          const typeText = normalizeText([product.type, product.category]);
          if (containsPhrase(titleText, state.query.term)) score += 42;
          else if (containsPhrase(typeText, state.query.term)) score += 34;
          else score += 24;
          reasons.push(`Matches your request for ${state.query.label.toLowerCase()}`);
        }
      }

      if (state.intent) {
        if (state.intent.specialMode === 'surprise') {
          score += (hashNumber(product.id) % 1000) / 1000;
          reasons.push('A less predictable pick from this collection');
        } else {
          let intentMatches = 0;
          (state.intent.keywords || []).forEach((rawKeyword) => {
            const keyword = normalizeText(rawKeyword).trim();
            if (keyword && containsPhrase(text, keyword)) intentMatches += 1;
          });
          if (intentMatches > 0) {
            score += Math.min(10, intentMatches * 2.5);
            reasons.push(`Fits your “${state.intent.label}” direction`);
          } else {
            exact = false;
          }

          if (state.intent.specialMode === 'container') {
            const locationText = normalizeText(product.locations);
            const containerFit = product.containerSuitable === true || locationText.includes('container');
            if (containerFit) {
              score += 8;
              reasons.push('Suited to container growing');
            } else {
              score -= 3;
              exact = false;
            }
          }


          const intentSignal = normalizeText([state.intent.label, state.intent.keywords]);
          const prefersFastResults = /fast|quick|early|short season|rapid|days to maturity|days to bloom/.test(intentSignal);
          if (prefersFastResults) {
            const maturity = this.productMaturity(product);
            const maturityDays = maturity.max ?? maturity.min;
            if (maturityDays !== null) {
              const timingBoost = Math.max(1, 12 - Math.min(11, Math.floor(maturityDays / 15)));
              score += timingBoost;
              const timingText = maturity.min === maturity.max || maturity.max === null
                ? `${maturity.min}`
                : `${maturity.min}–${maturity.max}`;
              reasons.push(`Listed around ${timingText} days`);
            } else {
              exact = false;
            }
          }
        }
      }

      if (state.location) {
        const selected = normalizeText(state.location.label);
        const locations = normalizeText(product.locations);
        const normalizedSelected = selected.replace(/s$/, '');
        if (locations.includes(selected) || locations.includes(normalizedSelected)) {
          score += 7;
          reasons.push(`Listed for ${state.location.label.toLowerCase()}`);
        } else if (selected.includes('container') && product.containerSuitable === true) {
          score += 7;
          reasons.push('Marked container suitable');
        } else {
          score -= 2;
          exact = false;
        }
      }

      if (state.sun) {
        const selected = normalizeText(state.sun.label);
        const productSun = normalizeText(product.sun);
        const normalizedSelected = selected.replace(/s$/, '');
        if (productSun.includes(selected) || productSun.includes(normalizedSelected) || text.includes(selected)) {
          score += 6;
          reasons.push(`Matches ${state.sun.label.toLowerCase()} conditions`);
        } else {
          score -= 1;
          exact = false;
        }
      }

      if (state.maturity) {
        const requested = parseRange(state.maturity.label);
        const productRange = this.productMaturity(product);
        if (requested.min !== null && productRange.min !== null) {
          const productMax = productRange.max ?? productRange.min;
          const overlaps = productRange.min <= requested.max && productMax >= requested.min;
          if (overlaps) {
            score += 7;
            reasons.push(`Maturity aligns with ${state.maturity.label.toLowerCase()}`);
          } else {
            return { product, score: -999, reasons, exact: false, excluded: true, season: null, queryMatched };
          }
        } else {
          exact = false;
        }
      }

      const seasonAssessment = this.assessSeasonFit(product, state.season);
      score += seasonAssessment.score;
      if (seasonAssessment.reason) reasons.push(seasonAssessment.reason);
      if (!seasonAssessment.exact) exact = false;

      if (product.available && state.availability?.mode === 'in-stock') {
        score += 3;
        reasons.push('Available now');
      }

      if (!reasons.length) reasons.push('A useful place to begin exploring this collection');
      return {
        product,
        score,
        reasons: reasons.slice(0, 4),
        exact,
        excluded: false,
        season: seasonAssessment,
        queryMatched
      };
    }

    async finish() {
      if (this.isFinishing) return;
      if (!this.validateSeasonStep()) {
        this.focusPlanning();
        return;
      }

      const state = this.getState();
      this.isFinishing = true;
      this.close();
      this.showLoading(state);

      try {
        let products = await this.productsForState(state);
        if (state.query?.term && !state.category) {
          products = products.filter((product) => this.productMatchesQuery(product, state.query));
        }

        const scored = products
          .map((product) => this.scoreProduct(product, state))
          .filter((item) => !item.excluded)
          .sort((a, b) => {
            if (state.intent?.specialMode === 'surprise') return b.score - a.score;
            if (b.score !== a.score) return b.score - a.score;
            if (a.product.available !== b.product.available) return a.product.available ? -1 : 1;
            return String(a.product.title).localeCompare(String(b.product.title));
          });

        const primaryPool = scored.filter((item) => item.season?.group !== 'later');
        const laterPool = scored.filter((item) => item.season?.group === 'later');
        const exactMatches = primaryPool.filter((item) => item.exact && item.score > 0);
        const max = Math.max(1, Number(this.config.maxRecommendations) || 6);
        const broadUnspecified = !state.query && !state.category && this.isBroadCollection();
        let recommendationPool;
        if (broadUnspecified) {
          const exactSet = new Set(exactMatches);
          recommendationPool = [
            ...exactMatches,
            ...primaryPool.filter((item) => !exactSet.has(item))
          ];
          if (!recommendationPool.length) recommendationPool = scored;
        } else {
          recommendationPool = exactMatches.length >= 3 ? exactMatches : (primaryPool.length ? primaryPool : scored);
        }
        const recommendations = this.selectDiverse(recommendationPool, max, state);
        const laterRecommendations = (state.season?.mode === 'now' || state.season?.mode === 'later')
          ? this.selectDiverse(laterPool, 3, state)
          : [];

        state.catalogFetchFailed = this.catalogFetchFailed;
        state.catalogProductCount = products.length;
        this.renderResults(recommendations, laterRecommendations, state, exactMatches.length);
      } finally {
        this.isFinishing = false;
      }

      if (this.results) {
        this.results.hidden = false;
        requestAnimationFrame(() => {
          this.results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
          this.results.focus({ preventScroll: true });
        });
      }
    }

    renderResults(recommendations, laterRecommendations, state, exactCount) {
      if (!this.resultGrid) return;
      this.resultGrid.replaceChildren();
      if (this.laterGrid) this.laterGrid.replaceChildren();

      if (!recommendations.length) {
        const empty = document.createElement('p');
        empty.className = 'aonsk-finder__empty';
        if (state.query?.label || state.category?.label) {
          const requested = state.query?.label || state.category?.label;
          const sampleIsLimited = Number(this.config.collectionProductCount || 0) > Number((this.config.products || []).length);
          if (state.catalogFetchFailed && sampleIsLimited) {
            empty.textContent = `We recognized ${requested}, but the broader catalog could not be loaded in this browser session. Use the button below to open the store results for ${requested}; unrelated products have not been substituted.`;
          } else {
            empty.textContent = `We recognized ${requested}, but no products in this collection matched confidently. Use the button below to view the matching collection or search results; unrelated products have not been substituted.`;
          }
        } else if (state.experienceCriteria?.length) {
          empty.textContent = 'No reviewed Seed Success Profiles in this collection matched every experience choice. Products without reviewed difficulty data have not been labeled or substituted. The full collection remains available below.';
        } else {
          empty.textContent = 'We do not have enough product information to make a confident shortlist yet. The full collection and regular filters are still available below.';
        }
        this.resultGrid.append(empty);
      } else {
        recommendations.forEach((item, index) => this.resultGrid.append(this.createCard(item, index)));
      }

      if (this.laterWrap) this.laterWrap.hidden = !laterRecommendations.length;
      if (this.laterGrid && laterRecommendations.length) {
        laterRecommendations.forEach((item, index) => this.laterGrid.append(this.createCard(item, index + recommendations.length, true)));
      }

      const selections = [];
      if (state.query?.label && !state.category) selections.push(state.query.label.toLowerCase());
      if (state.category?.label) selections.push(state.category.label.toLowerCase());
      if (state.intent?.label) selections.push(state.intent.label.toLowerCase());
      if (state.location?.label) selections.push(state.location.label.toLowerCase());
      if (state.sun?.label) selections.push(state.sun.label.toLowerCase());
      if (state.maturity?.label) selections.push(state.maturity.label.toLowerCase());
      if (state.availability?.mode === 'in-stock') selections.push('currently available products');
      if (state.experienceCriteria?.length) selections.push(...state.experienceCriteria.map((item) => item.label.toLowerCase()));

      if (this.resultsHeading) {
        const requested = state.query?.label || state.category?.label;
        this.resultsHeading.textContent = requested
          ? `${requested} options we would look at first.`
          : state.intent?.specialMode === 'surprise'
            ? `Here are a few ${this.config.resultPlural || 'products'} worth discovering.`
            : `Here are a few ${this.config.resultPlural || 'products'} we would look at first.`;
      }

      if (this.resultsSummary) {
        const seasonReminder = state.season?.record && (state.season.mode === 'now' || state.season.mode === 'later')
          ? ` This shortlist reflects the selected planting window, not all ${this.config.resultPlural || 'products'} that may suit your area.`
          : ' The complete collection remains available below.';
        if (!selections.length) {
          this.resultsSummary.textContent = `We kept the search broad and selected a few useful starting points from ${this.config.collectionTitle}.${seasonReminder}`;
        } else if (exactCount >= 3) {
          this.resultsSummary.textContent = `These recommendations best reflect ${this.joinWords(selections)}.${seasonReminder}`;
        } else {
          this.resultsSummary.textContent = `Only a few products matched every detail, so we also included the closest useful options for ${this.joinWords(selections)}.${seasonReminder}`;
        }
      }

      this.renderSeasonResult(state.season);

      if (this.browseLink) {
        const url = this.buildFilterUrl(state);
        this.browseLink.href = url;
        const filterCount = [state.category, state.location, state.sun, state.maturity, state.availability?.mode === 'in-stock' ? state.availability : null].filter(Boolean).length;
        const requested = state.query?.label || state.category?.label;
        this.browseLink.textContent = requested
          ? `Show ${requested} products`
          : filterCount > 0
            ? 'Open the collection with these filters'
            : `Browse all ${this.config.collectionTitle}`;
      }
    }

    renderSeasonResult(season) {
      if (!this.seasonResult) return;
      if (!season?.record) {
        this.seasonResult.hidden = true;
        this.seasonResult.textContent = '';
        return;
      }

      let heading = 'Seasonal planning view';
      let text = '';
      if (season.mode === 'next-year') {
        heading = 'Planning for next year';
        text = `For ZIP ${season.zip}, the broad frost-free window is approximately ${formatDate(season.lastFrost)} through ${formatDate(season.firstFrost)}. Current-season timing did not narrow your results.`;
      } else if (season.mode === 'all-area') {
        heading = 'Full-season view for your area';
        text = `For ZIP ${season.zip}, the guide compared listed maturity with the broad season from about ${formatDate(season.lastFrost)} through ${formatDate(season.firstFrost)}, without limiting results to one date.`;
      } else if (season.phase === 'active') {
        heading = 'What may still fit this planting window';
        text = `For ZIP ${season.zip}, the selected date leaves roughly ${season.daysRemaining} days before the broad average first-frost estimate of ${formatDate(season.firstFrost)}.`;
      } else if (season.phase === 'before-season') {
        heading = this.config.collectionSeasonClass === 'cool'
          ? 'The selected date is before the main frost-free window'
          : 'The selected date is early for frost-tender planting';
        text = `For ZIP ${season.zip}, the broad average last-frost estimate is around ${formatDate(season.lastFrost)}. Results include varieties to consider after that point.`;
      } else if (season.phase === 'after-season') {
        heading = 'The selected date is beyond the broad frost-free window';
        text = `For ZIP ${season.zip}, the broad average first-frost estimate is around ${formatDate(season.firstFrost)}. Results also show varieties worth saving for next season.`;
      } else {
        text = `For ZIP ${season.zip}, the guide used an approximate frost window to support—not replace—your own local judgment.`;
      }

      this.seasonResult.hidden = false;
      this.seasonResult.innerHTML = `<strong>${heading}</strong><span>${text} This is not live weather, and it is not the only set of products that may suit your area.</span>`;
    }

    createCard(item, index, compact = false) {
      const product = item.product;
      const article = document.createElement('article');
      article.className = `aonsk-finder__result-card${compact ? ' is-compact' : ''}`;

      const mediaLink = document.createElement('a');
      mediaLink.className = 'aonsk-finder__result-media';
      mediaLink.href = product.url;
      mediaLink.setAttribute('aria-label', product.title);
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.imageAlt || product.title;
        image.loading = index < 2 ? 'eager' : 'lazy';
        image.width = 720;
        image.height = 540;
        mediaLink.append(image);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'aonsk-finder__result-fallback';
        fallback.textContent = this.config.resultNoun ? this.config.resultNoun.charAt(0).toUpperCase() + this.config.resultNoun.slice(1) : 'Item';
        mediaLink.append(fallback);
      }

      const status = document.createElement('span');
      status.className = `aonsk-finder__status ${product.available ? 'is-available' : 'is-sold-out'}`;
      status.textContent = product.available ? 'Available now' : 'Currently sold out';
      mediaLink.append(status);

      if (item.season?.label) {
        const seasonTag = document.createElement('span');
        seasonTag.className = `aonsk-finder__season-tag ${item.season.className || ''}`;
        seasonTag.textContent = item.season.label;
        mediaLink.append(seasonTag);
      }

      const body = document.createElement('div');
      body.className = 'aonsk-finder__result-body';

      if (product.experienceLabel) {
        const experienceBadge = document.createElement('p');
        experienceBadge.className = 'aonsk-finder__experience-badge';
        experienceBadge.textContent = product.experienceLabel;
        body.append(experienceBadge);
      }

      const reasonLabel = document.createElement('p');
      reasonLabel.className = 'aonsk-finder__result-kicker';
      reasonLabel.textContent = 'Why it may fit';

      const heading = document.createElement('h4');
      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      heading.append(titleLink);

      const reasonList = document.createElement('ul');
      item.reasons.forEach((reason) => {
        const listItem = document.createElement('li');
        listItem.textContent = reason;
        reasonList.append(listItem);
      });

      const footer = document.createElement('div');
      footer.className = 'aonsk-finder__result-footer';
      const price = document.createElement('strong');
      price.textContent = product.price;
      const view = document.createElement('a');
      view.href = product.url;
      view.textContent = this.config.mode === 'market' ? 'View details' : 'View seed details';
      footer.append(price, view);

      body.append(reasonLabel, heading, reasonList, footer);
      article.append(mediaLink, body);
      return article;
    }

    buildFilterUrl(state) {
      const browserOrigin = window.location.origin && window.location.origin !== 'null'
        ? window.location.origin
        : 'https://aonsk-seed-finder.invalid';
      const url = new URL(this.config.collectionUrl || window.location.pathname, browserOrigin);
      const addFilter = (filter) => {
        if (!filter?.param || filter.value === 'all') return;
        url.searchParams.append(filter.param, filter.value);
      };
      if (state.query?.term && !state.category) {
        const searchUrl = new URL('/search', browserOrigin);
        searchUrl.searchParams.set('q', state.query.term);
        searchUrl.searchParams.set('type', 'product');
        if (searchUrl.hostname === 'aonsk-seed-finder.invalid') return `${searchUrl.pathname}${searchUrl.search}`;
        return searchUrl.toString();
      }
      addFilter(state.category);
      addFilter(state.location);
      addFilter(state.sun);
      addFilter(state.maturity);
      if (state.availability?.mode === 'in-stock') addFilter(state.availability);
      url.hash = 'filter-results';
      if (url.hostname === 'aonsk-seed-finder.invalid') return `${url.pathname}${url.search}${url.hash}`;
      return url.toString();
    }

    joinWords(items) {
      if (items.length <= 1) return items[0] || '';
      if (items.length === 2) return `${items[0]} and ${items[1]}`;
      return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
    }

    scrollToProducts() {
      const target = document.querySelector(this.config.productsAnchor || '#filter-results');
      target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }

    reset() {
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = input.dataset.availability === 'all' || input.dataset.planMode === 'now' || input.dataset.maturityAuto === 'true';
      });
      this.syncChoiceStates();
      if (this.note) this.note.value = '';
      this.naturalQuery = '';
      this.specificQuery = null;
      this.querySelectorAll('[data-selected-by-note="true"]').forEach((input) => {
        input.checked = false;
        delete input.dataset.selectedByNote;
      });
      this.updateQueryRecognition();
      if (this.planSelect) this.planSelect.value = 'now';
      if (this.dateInput) this.dateInput.value = '';
      this.querySelectorAll('[data-finder-quick]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-selected');
      });
      this.querySelectorAll('[data-finder-experience]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-selected');
      });
      this.currentStep = 0;
      this.updateDateVisibility();
      this.updateSeasonPreview();
      this.updateMaturityGuidance();
      this.updateStep();
      if (this.results) this.results.hidden = true;
      this.open();
    }
  }

  if (!customElements.get('aonsk-seed-finder')) {
    customElements.define('aonsk-seed-finder', AonskSeedFinder);
  }
})();

